import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Send, X } from 'lucide-react';
import { useError } from '../context/ErrorContext';

const TOAST_AUTO_DISMISS_MS = 10000; // 10 saniye sonra otomatik kapanır

const ErrorPopup: React.FC = () => {
  const { error, clearError } = useError();
  const [reportCopied, setReportCopied] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(clearError, TOAST_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [error, clearError]);

  const handleReport = useCallback(() => {
    const text = `${t('errorPopup.reportHeader')}\nCorrelation ID: ${error?.correlationId ?? t('errorPopup.none')}`;
    navigator.clipboard.writeText(text).then(() => {
      setReportCopied(true);
      setTimeout(() => setReportCopied(false), 2500);
    });
  }, [error?.correlationId, t]);

  if (!error) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[100] max-w-sm toast-slide-in"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3 p-3 rounded-lg shadow-lg border border-red-200 dark:border-red-800 bg-white dark:bg-gray-800">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          {error.isUnexpected && (
            <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-0.5">
              {t('errorPopup.systemError')}
            </p>
          )}
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            {error.message}
          </p>
          {error.isUnexpected && (
            <div className="mt-2 flex items-center gap-2 flex-wrap justify-end">
              {error.correlationId && (
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono mr-auto">
                  ID: {error.correlationId}
                </span>
              )}
              <button
                type="button"
                onClick={handleReport}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors flex-shrink-0"
              >
                <Send className="h-3.5 w-3.5" />
                {reportCopied ? t('errorPopup.copied') : t('errorPopup.report')}
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={clearError}
          className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
          aria-label={t('common.close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ErrorPopup;
